import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { RefreshCw, Save, AlertCircle } from "lucide-react";
import { api } from "@/services/api";
import type { UsageStatsResponse } from "@/services/api";

interface SettingsState {
  daily_evaluation_limit: number;
  editing: boolean;
  editValue: string;
  saving: boolean;
}

export default function AdminStatsPage() {
  const [stats, setStats] = useState<UsageStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<SettingsState>({
    daily_evaluation_limit: 50,
    editing: false,
    editValue: "50",
    saving: false,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usageRes, settingsRes] = await Promise.all([
        api.getUsageStats(30),
        api.getAdminSettings(),
      ]);
      setStats(usageRes);
      setSettings(prev => ({
        ...prev,
        daily_evaluation_limit: settingsRes.settings.daily_evaluation_limit,
        editValue: String(settingsRes.settings.daily_evaluation_limit),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveSettings = async () => {
    const value = parseInt(settings.editValue, 10);
    if (isNaN(value) || value < 1 || value > 1000) return;

    setSettings(prev => ({ ...prev, saving: true }));
    try {
      const res = await api.updateAdminSettings({ daily_evaluation_limit: value });
      setSettings(prev => ({
        ...prev,
        daily_evaluation_limit: res.settings.daily_evaluation_limit,
        editing: false,
        saving: false,
      }));
    } catch {
      setSettings(prev => ({ ...prev, saving: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading stats...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20 text-destructive">
        <AlertCircle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Settings Panel */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Rate Limit Settings</h3>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Daily evaluation limit per user:</span>
          {settings.editing ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={1000}
                value={settings.editValue}
                onChange={(e) => setSettings(prev => ({ ...prev, editValue: e.target.value }))}
                className="w-24 px-3 py-1.5 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveSettings();
                  if (e.key === "Escape") setSettings(prev => ({ ...prev, editing: false, editValue: String(prev.daily_evaluation_limit) }));
                }}
                autoFocus
              />
              <button
                onClick={handleSaveSettings}
                disabled={settings.saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {settings.saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, editing: false, editValue: String(prev.daily_evaluation_limit) }))}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-foreground">{settings.daily_evaluation_limit}</span>
              <button
                onClick={() => setSettings(prev => ({ ...prev, editing: true }))}
                className="px-3 py-1.5 text-sm text-primary hover:text-primary/80 font-medium"
              >
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Daily Evaluations Chart */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Daily Evaluations (Last 30 Days)</h3>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
        {stats && stats.dailyTotals.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={stats.dailyTotals}>
              <defs>
                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(value: string) => {
                  const d = new Date(value + "T00:00:00");
                  return `${d.getMonth() + 1}/${d.getDate()}`;
                }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  color: "hsl(var(--foreground))",
                }}
                labelFormatter={(label) => {
                  const d = new Date(String(label) + "T00:00:00");
                  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="hsl(var(--primary))"
                fillOpacity={1}
                fill="url(#colorCount)"
                strokeWidth={2}
                name="Evaluations"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
            No usage data available yet.
          </div>
        )}
      </div>

      {/* Per-User Usage Table */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Per-User Usage (Last 30 Days)</h3>
        {stats && stats.perUser.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Email</th>
                  <th className="text-right py-3 px-4 text-muted-foreground font-medium">Today</th>
                  <th className="text-right py-3 px-4 text-muted-foreground font-medium">30-Day Total</th>
                </tr>
              </thead>
              <tbody>
                {stats.perUser.map((user) => (
                  <tr key={user.userId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4 text-foreground">{user.email}</td>
                    <td className="py-3 px-4 text-right text-foreground tabular-nums">{user.todayCount}</td>
                    <td className="py-3 px-4 text-right text-foreground tabular-nums font-medium">{user.totalCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No usage data available yet.</p>
        )}
      </div>
    </div>
  );
}
